import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Tooltip from '@mui/material/Tooltip';
import {
  FaEdit,
  FaShieldAlt,
} from 'react-icons/fa';
import reportService from '../../services/reportService';
import { LoadingSpinner } from '../../components';
import NoData from '../../components/NoData';
import HeaderTitle from '../../components/HeaderTitle';
import { setToastMessage } from '../../reducers/common.reducer';
import EditLimitDialog from './components/EditLimitDialog';
import { useWindowWidth } from '@react-hook/window-size';

const currencyFormatter = new Intl.NumberFormat('en-US');

const columns = [
  'Type', 
  'Team Count', 
  //'Risk Amount', 
  'Bet Limit', 
  'Risk Bet Amount', 
  'Action'
];

const getLimitTypeLabel = (gamblingTypeId) =>
  Number(gamblingTypeId) === 1 ? 'single' : 'mix';

const GamblingWinLimit = () => {
  const dispatch = useDispatch();
  const onlyWidth = useWindowWidth();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [limitData, setLimitData] = useState([]);
  const [editedData, setEditedData] = useState();
  const [saveLoading, setSaveLoading] = useState(false);
  const [minLimit, setMinLimit] = useState([]);
  const [editMode, setEditMode] = useState('limit');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await reportService.getminmax2();

      if (response.status === 200) {
        //console.log("aaaaaa>>", response.data.data);
        setLimitData(response.data.gamblingLimit || []);
        setMinLimit(response.data.gamblingType || [])
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = (data) => {
    const filteredData = limitData.find(
      (item) => item.gamblingLimitId === data.gamblingLimitId
    );
    setEditedData(filteredData);
    setEditMode('limit');
    setOpen(true);
  };

  const handleMinLimitOpen = (data) => {
    setEditedData({ ...data });
    setEditMode('minimum');
    setOpen(true);
  };

  const handleSave = async (data) => {
    setSaveLoading(true);
    try {
      const payload = {
        teamCount: data.teamCount,
        amount: parseInt(data.amount, 10),
        riskBetLimit : parseInt(data.riskBetLimit, 10),
      };
      const response = await reportService.updateGamblingLimit(payload);
      if (response.status === 200) {
        dispatch(
          setToastMessage({ type: 'success', message: 'Updated Successfully!' })
        );
        setOpen(false);
        fetchData();
      } else {
        dispatch(
          setToastMessage({ type: 'error', message: response.data.message })
        );
      }
    } catch (err) {
      console.log('error>>>', err.message);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleMinLimitSave = async (data) => {
    setSaveLoading(true);
    try {
      const payload = {
        id: data.gamblingTypeId,
        gamblingType: data.gamblingType,
        maxBetAmount: Number(data.maxBetAmount),
        minBetAmount: Number(data.minBetAmount),
      };
      const response = await reportService.updateminmax2(payload);

      if (response.status === 200) {
        dispatch(
          setToastMessage({ type: 'success', message: 'Minimum limit updated successfully!' })
        );
        setOpen(false);
        fetchData();
      } else {
        dispatch(
          setToastMessage({
            type: 'error',
            message: response.data?.message || 'Unable to update the minimum limit.',
          })
        );
      }
    } catch (err) {
      dispatch(
        setToastMessage({
          type: 'error',
          message: err.response?.data?.message || 'Unable to update the minimum limit.',
        })
      );
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="mx-3 space-y-5 pb-6 font-inter">
      <EditLimitDialog
        open={open}
        setOpen={setOpen}
        editedData={editedData}
        setEditedData={setEditedData}
        handleSave={editMode === 'minimum' ? handleMinLimitSave : handleSave}
        saveLoading={saveLoading}
        editMode={editMode}
      />

      <div className="my-3">
        <HeaderTitle title="Gambling Limit" width={onlyWidth} />
      </div>

      <section className="overflow-hidden rounded-[28px] border border-slate-200/70 bg-white/90 shadow-[0_20px_60px_-24px_rgba(15,23,42,0.35)] dark:border-white/10 dark:bg-custom_dark_primary2/90">
        <div className="flex flex-col gap-4 border-b border-slate-200/70 bg-gradient-to-r from-sky-50 via-white to-emerald-50 px-5 py-5 dark:border-white/10 dark:from-custom_dark_background dark:via-custom_dark_primary2 dark:to-customSurface sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sky-100 text-customAccentBlue dark:bg-customAccent/15 dark:text-customAccent">
              <FaShieldAlt className="text-lg" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-customWhite sm:text-2xl">
                Overall Gambling Minimum Limit
              </h2>
              <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-customMuted">
                Minimum accepted amount configured for each gambling type.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-sky-200/70 bg-white/80 px-4 py-3 text-sm leading-6 text-slate-600 shadow-sm dark:border-customAccent/20 dark:bg-white/5 dark:text-customMuted sm:max-w-xl">
            <span className="font-semibold text-slate-900 dark:text-customWhite">
              Validation workflow:
            </span>{' '}
           Validate the user-specific betting limit first, followed by validation of the system-wide betting limit based on the applicable team count.
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-slate-200/70 bg-slate-50/80 text-left text-xs uppercase tracking-[0.2em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-customMuted">
                <th className="px-5 py-4 sm:px-6">Gambling Type</th>
                <th className="px-5 py-4 sm:px-6">Minimum Amount</th>
                <th className="px-5 py-4 text-right sm:px-6">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={3} className="h-24">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : minLimit.length > 0 ? (
                minLimit.map((item) => (
                <tr
                  key={item.gamblingTypeId}
                  className="border-b border-slate-100 text-sm text-slate-700 last:border-b-0 dark:border-white/5 dark:text-customWhite"
                >
                  <td className="px-5 py-4 sm:px-6">
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${item.gamblingTypeId === 1 ? 'bg-sky-500/10 text-sky-800 dark:text-sky-400' : 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-400'}`}
                    >
                      {item.gamblingType}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-base font-semibold text-slate-900 dark:text-customWhite sm:px-6">
                    {item.minBetAmount !== null && item.minBetAmount !== undefined
                      ? currencyFormatter.format(Number(item.minBetAmount || 0))
                      : '—'}
                  </td>
                  <td className="px-5 py-4 text-right sm:px-6">
                    <Tooltip
                      title={`Edit ${item.gamblingType} minimum limit`}
                      placement="top"
                      arrow
                    >
                      <span>
                        <button
                          type="button"
                          onClick={() => handleMinLimitOpen(item)}
                          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:-translate-y-0.5 hover:border-sky-300 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 dark:border-white/10 dark:bg-white/5 dark:text-customWhite dark:hover:border-cyan-400/50 dark:hover:text-cyan-300"
                        >
                          <FaEdit className="text-sm" />
                          Edit
                        </button>
                      </span>
                    </Tooltip>
                  </td>
                </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="py-10">
                    <NoData />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="overflow-hidden rounded-[28px] border border-slate-200/70 bg-white/90 shadow-[0_18px_45px_-24px_rgba(15,23,42,0.3)] backdrop-blur-sm dark:border-white/10 dark:bg-custom_dark_primary2/80">
        <div className="flex flex-col gap-2 border-b border-slate-200/70 px-5 py-4 dark:border-white/10">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl text-slate-900 dark:text-customWhite">
                Overall Gambling Maximum Limit
              </h2>
              <p className="text-sm text-slate-500 dark:text-customMuted">
                Current rules grouped for quick review and edits.
              </p>
            </div>
            <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-customAccentBlue dark:text-customAccent dark:bg-customAccent/10">
              {limitData.length} Records
            </div>
          </div>
        </div>

        <div className="hidden lg:block">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-200/70 bg-slate-50/80 text-left text-xs uppercase tracking-[0.24em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-customMuted">
                  <th className="px-6 py-4">No</th>
                  {columns.map((label) => (
                    <th key={label} className="px-6 py-4">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="h-24">
                      <LoadingSpinner />
                    </td>
                  </tr>
                ) : limitData.length > 0 ? (
                  limitData.map((item, index) => (
                    <tr
                      key={item.gamblingLimitId || index}
                      className="border-b border-slate-100 text-sm text-slate-700 transition-colors hover:bg-slate-50/80 dark:border-white/5 dark:text-customWhite dark:hover:bg-white/5"
                    >
                      <td className="px-6 py-2 font-semibold text-slate-400 dark:text-customMuted">
                        {String(index + 1).padStart(2, '0')}
                      </td>
                      <td className="px-6 py-2">
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold tracking-[0.18em] ${item.gamblingTypeId === 1 ? 'text-sky-800 bg-sky-500/10 dark:bg-sky-500/5 dark:text-sky-500' : 'text-emerald-800 bg-emerald-500/10 dark:bg-emerald-500/5 dark:text-emerald-400'}`}>
                          {getLimitTypeLabel(item.gamblingTypeId)}
                        </span>
                      </td>
                      <td className="px-6 py-2 font-semibold">
                        {item.teamCount}
                      </td>
                      {/* <td className="px-6 py-2 text-base text-slate-900 dark:text-customWhite">
                        {currencyFormatter.format(Number(item.amount || 0))}
                      </td> */}
                       <td className="px-6 py-2 text-base text-slate-900 dark:text-customWhite">
                        {currencyFormatter.format(Number(item.betLimit || 0))}
                      </td>
                       <td className="px-6 py-2 text-base text-slate-900 dark:text-customWhite">
                        {currencyFormatter.format(Number(item.riskBetLimit || 0))}
                      </td>
                      <td className="px-6 py-2">
                        <Tooltip title="Edit limit" placement="top" arrow>
                          <button
                            type="button"
                            onClick={() => handleOpen(item)}
                            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:-translate-y-0.5 hover:border-sky-200 hover:text-sky-700 dark:border-white/10 dark:bg-white/5 dark:text-customWhite dark:hover:border-cyan-400/50 dark:hover:text-cyan-300"
                          >
                            <FaEdit className="text-sm" />
                            Edit
                          </button>
                        </Tooltip>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="py-10">
                      <NoData />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid gap-4 p-4 lg:hidden">
          {isLoading ? (
            <LoadingSpinner />
          ) : limitData.length > 0 ? (
            limitData.map((item, index) => (
              <article
                key={item.gamblingLimitId || index}
                className="overflow-hidden rounded-3xl border border-slate-200/70 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm dark:border-white/10 dark:bg-gradient-to-br dark:from-custom_dark_primary dark:to-custom_dark_primary2"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.24em] text-slate-400 dark:text-customMuted">
                      Rule {String(index + 1).padStart(2, '0')}
                    </p>
                    <div className="mt-2 flex items-center gap-3">
                      <p className="mt-2 text-xl text-slate-900 dark:text-customWhite">
                        {currencyFormatter.format(Number(item.amount || 0))}
                      </p>
                      <p className="mt-2 text-base text-slate-900 dark:text-customWhite">
                       (Bet Limit : {currencyFormatter.format(Number(item.riskBetLimit || 0))})
                      </p>
                    </div>
                  </div>
                  <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-sky-800 dark:bg-sky-500/10 dark:text-cyan-300">
                    {getLimitTypeLabel(item.gamblingTypeId)}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-2xl bg-slate-100/80 p-3 dark:bg-white/5">
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-customMuted">
                      Team Count
                    </p>
                    <p className="mt-2 font-semibold text-slate-800 dark:text-customWhite">
                      {item.teamCount}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-slate-100/80 p-3 dark:bg-white/5">
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-customMuted">
                      Limit Type
                    </p>
                    <p className="mt-2 font-semibold text-slate-800 dark:text-customWhite">
                      {getLimitTypeLabel(item.gamblingTypeId)}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleOpen(item)}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-sky-700 dark:bg-customDarkGreen dark:hover:bg-emerald-500"
                >
                  <FaEdit className="text-sm" />
                  Edit Limit
                </button>
              </article>
            ))
          ) : (
            <NoData />
          )}
        </div>
      </section>
    </div>
  );
};

export default GamblingWinLimit;
